(() => {
  if (customElements.get('anorak-collection-scroll')) return;
  const scope = '[data-anorak-collection]';
  const stateKey = () => location.pathname + location.search;
  const states = new WeakMap();
  const stateFor = section => {
    if (!states.has(section)) states.set(section, {version: 0, busy: false});
    return states.get(section);
  };
  const finishImages = container => {
    container.querySelectorAll('img.is-loading').forEach(image => {
      if (!image.complete) return;
      image.classList.remove('is-loading');
      image.parentElement?.classList.remove('is-loading');
      image.parentElement?.parentElement?.classList.remove('is-loading');
    });
  };
  const reviews = () => {
    try {
      if (typeof window.jdgm?.refreshWidgets === 'function') window.jdgm.refreshWidgets();
    } catch (error) { console.warn('Anorak review refresh:', error); }
  };
  class CollectionScroll extends HTMLElement {
    connectedCallback() {
      this.section = this.closest(scope);
      this.grid = this.section?.querySelector('#CollectionLoop');
      this.controls = this.querySelector('[data-scroll-controls]');
      this.button = this.querySelector('[data-scroll-load]');
      this.pauseButton = this.querySelector('[data-scroll-pause]');
      this.status = this.querySelector('[data-scroll-status]');
      this.pagination = this.querySelector('[data-anorak-pagination]');
      if (!this.grid || !this.controls) return;
      this.key = stateKey();
      this.seen = new Set([...this.grid.children].map(card => card.id).filter(Boolean));
      this.visited = new Set();
      this.paused = false;
      this.failed = false;
      this.loading = false;
      this.next = this.dataset.next;
      this.page = Number(this.dataset.page);
      this.manual = () => this.load(true);
      this.toggle = () => {
        this.paused = !this.paused;
        this.toggleAttribute('data-scroll-paused', this.paused);
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.observe();
      };
      this.onPopstate = () => { this.request?.abort(); this.observer?.disconnect(); };
      this.button.addEventListener('click', this.manual);
      this.pauseButton.addEventListener('click', this.toggle);
      window.addEventListener('popstate', this.onPopstate);
      this.toggleAttribute('data-scroll-auto', 'IntersectionObserver' in window);
      this.removeAttribute('data-scroll-paused');
      this.controls.hidden = !this.next;
      this.button.hidden = !this.next;
      this.pauseButton.hidden = !this.next || !('IntersectionObserver' in window);
      this.pagination.hidden = true;
      this.removeAttribute('data-scroll-notice');
      this.observe();
      finishImages(this.grid);
    }
    observe() {
      this.observer?.disconnect();
      if (!this.isConnected || stateKey() !== this.key || !this.next || this.paused || this.failed || stateFor(this.section).busy || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) this.load(false);
      }, {rootMargin:'0px 0px 600px 0px'});
      this.observer.observe(this);
    }
    async load(manual) {
      const state = stateFor(this.section);
      if (!this.isConnected || this.loading || state.busy || stateKey() !== this.key || !this.next) return;
      if (!manual && (this.paused || this.failed)) return;
      const url = new URL(this.next, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname || this.visited.has(url.href)) return;
      const version = state.version;
      this.observer?.disconnect();
      this.loading = true;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.removeAttribute('data-scroll-notice');
      this.status.textContent = this.dataset.loading;
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 20000);
      try {
        const response = await fetch(url.href, {signal:this.request.signal});
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || state.busy || state.version !== version || stateKey() !== this.key) return;
        const section = doc.getElementById(this.section.id);
        const grid = section?.querySelector('#CollectionLoop');
        const pager = section?.querySelector('anorak-collection-scroll');
        if (!grid || !pager || Number(pager.dataset.page) !== this.page + 1) throw new Error('Invalid collection page');
        const fragment = document.createDocumentFragment();
        let first;
        for (const card of [...grid.children]) {
          if (!card.matches('[data-grid-item]') || (card.id && this.seen.has(card.id))) continue;
          if (card.id) this.seen.add(card.id);
          first ||= card;
          fragment.append(card);
        }
        this.grid.append(fragment);
        finishImages(this.grid);
        this.page = Number(pager.dataset.page);
        this.dataset.page = String(this.page);
        this.visited.add(url.href);
        this.next = pager.dataset.next;
        this.dataset.next = this.next;
        this.pagination.replaceChildren(...pager.querySelector('[data-anorak-pagination]').childNodes);
        this.pagination.hidden = true;
        this.failed = false;
        this.toggleAttribute('data-scroll-notice', !this.next);
        this.status.textContent = this.next ? this.dataset.loaded : this.dataset.finished;
        this.button.hidden = !this.next;
        this.pauseButton.hidden = !this.next || !('IntersectionObserver' in window);
        if (manual && first) {
          const link = first.querySelector('a[href]');
          link?.focus({preventScroll:true});
        }
        reviews();
        this.dispatchEvent(new CustomEvent('anorak:collection:append', {bubbles:true,detail:{page:this.page}}));
      } catch (error) {
        if (this.isConnected && state.version === version && stateKey() === this.key && !state.busy) {
          this.failed = true;
          this.observer?.disconnect();
          this.pagination.hidden = false;
          this.setAttribute('data-scroll-notice', '');
          this.status.textContent = this.dataset.error;
        }
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
        this.observe();
      }
    }
    disconnectedCallback() {
      this.request?.abort();
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.manual);
      this.pauseButton?.removeEventListener('click', this.toggle);
      window.removeEventListener('popstate', this.onPopstate);
    }
  }
  customElements.define('anorak-collection-scroll', CollectionScroll);
  customElements.whenDefined('collection-filters-form').then(() => {
    const prototype = customElements.get('collection-filters-form').prototype;
    const original = prototype.submitForm;
    prototype.submitForm = function(event, href = '') {
      const section = this.closest(scope);
      if (!section) return original.call(this, event, href);
      if (!event || event.type !== 'popstate') {
        const url = new URL(href || location.href, location.href);
        if (!href) {
          for (const key of [...url.searchParams.keys()]) {
            if (key.startsWith('filter.') || key === 'page' || key === 'sort_by') url.searchParams.delete(key);
          }
          if (this.form) {
            for (const [key, value] of new FormData(this.form)) {
              if ((key.startsWith('filter.') || key === 'sort_by') && value) url.searchParams.append(key,value);
            }
            const min = this.form.querySelector('[data-se-min]')?.getAttribute('data-se-min');
            const max = this.form.querySelector('[data-se-max]')?.getAttribute('data-se-max');
            if (url.searchParams.get('filter.v.price.gte') === min && url.searchParams.get('filter.v.price.lte') === max) {
              url.searchParams.delete('filter.v.price.gte');
              url.searchParams.delete('filter.v.price.lte');
            }
          }
        }
        if (url.origin !== location.origin) return;
        history.pushState(null, '', url.pathname + url.search);
      } else if (this.sort) this.onSortCheck(event);
      const state = stateFor(section);
      state.version++;
      clearTimeout(state.rearm);
      const version = state.version;
      state.busy = true;
      state.request?.abort();
      state.request = new AbortController();
      const signal = state.request.signal;
      const key = stateKey();
      const grid = section.querySelector('[data-products-grid]');
      const scroll = section.querySelector('anorak-collection-scroll');
      scroll?.request?.abort();
      scroll?.observer?.disconnect();
      grid.classList.add('is-loading');
      grid.setAttribute('aria-busy', 'true');
      const timeout = setTimeout(() => state.request.abort(), 20000);
      return fetch(key, {signal}).then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      }).then(html => {
        if (signal.aborted || !section.isConnected || version !== state.version || key !== stateKey()) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = doc.getElementById(section.id);
        const products = replacement?.querySelector('[data-products-grid]');
        if (!products) throw new Error('Invalid collection response');
        grid.innerHTML = products.innerHTML;
        const count = section.querySelector('[data-results-count]');
        const returnedCount = replacement.querySelector('[data-results-count]');
        if (count && returnedCount) count.innerHTML = returnedCount.innerHTML;
        if (this.collectionSidebar) {
          this.collectionSidebar.innerHTML = replacement.querySelector('[data-collection-sidebar]').innerHTML;
          const active = this.collectionSidebar.querySelector('[data-active-filters-count]');
          if (active) section.querySelectorAll('[data-active-filters]').forEach(label => {
            label.textContent = active.dataset.activeFiltersCount;
            label.classList.toggle('hidden', Number(active.dataset.activeFiltersCount) < 1);
          });
        }
        finishImages(grid);
        reviews();
        const nav = section.querySelector('[data-collection-nav]');
        const header = document.querySelector('[data-header-sticky]') ? document.querySelector('[data-header-height]')?.offsetHeight || 0 : 0;
        window.scrollTo({top:grid.getBoundingClientRect().top + scrollY - (nav?.offsetHeight || 0) - header,behavior:'smooth'});
      }).catch(error => {
        if (version !== state.version || !section.isConnected || key !== stateKey()) return;
        const message = scroll?.dataset.error;
        if (scroll?.status) {
          scroll.failed = true;
          scroll.pagination.hidden = false;
          scroll.setAttribute('data-scroll-notice', '');
          scroll.status.textContent = message;
          const retry = document.createElement('a');
          retry.className = 'btn btn--primary btn--outline';
          retry.href = key;
          retry.textContent = scroll.dataset.reload;
          scroll.status.append(' ',retry);
          scroll.controls.hidden = false;
          scroll.button.hidden = true;
          scroll.pauseButton.hidden = true;
        }
      }).finally(() => {
        clearTimeout(timeout);
        if (version !== state.version) return;
        state.busy = false;
        grid.classList.remove('is-loading');
        grid.removeAttribute('aria-busy');
        if (!signal.aborted) state.rearm = setTimeout(() => {
          if (version === state.version && section.isConnected) section.querySelector('anorak-collection-scroll')?.observe();
        }, 600);
      });
    };
  });
  document.addEventListener('shopify:section:unload', event => {
    const section = event.target.matches(scope) ? event.target : event.target.querySelector(scope);
    if (!section) return;
    const state = states.get(section);
    if (state) { state.version++; clearTimeout(state.rearm); state.request?.abort(); }
    section.querySelector('anorak-collection-scroll')?.disconnectedCallback();
  });
})();
